import React, { useState, useRef, useEffect, useCallback } from 'react';
import {
  Box, Input, List, ListItem, Text, Image, Flex, Tooltip, useDisclosure, Portal,
} from '@chakra-ui/react';
import api from '../api';
import CompanyModal from './CompanyModal';

export default function CompanySearch({ value, companyId, onSelect, logoUrl }) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [editing, setEditing] = useState(false);
  const [highlightIdx, setHighlightIdx] = useState(-1);
  const inputRef = useRef(null);
  const dropdownRef = useRef(null);
  const companyModal = useDisclosure();
  const [pendingName, setPendingName] = useState('');
  const debounceRef = useRef(null);

  const search = useCallback(async (q) => {
    if (q.length < 1) { setResults([]); return; }
    try {
      const { data } = await api.get(`/companies/search?q=${encodeURIComponent(q)}`);
      setResults(data);
      setShowDropdown(true);
      setHighlightIdx(-1);
    } catch (err) {
      console.error('Search error:', err);
    }
  }, []);

  const handleChange = (e) => {
    const val = e.target.value;
    setQuery(val);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => search(val), 200);
  };

  const handleSelect = (company) => {
    setQuery('');
    setEditing(false);
    setShowDropdown(false);
    onSelect(company);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlightIdx(prev => Math.min(prev + 1, results.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlightIdx(prev => Math.max(prev - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (highlightIdx >= 0 && results[highlightIdx]) {
        handleSelect(results[highlightIdx]);
      } else if (query.length > 0 && results.length === 0) {
        // No match found - open modal to create new company
        setPendingName(query);
        companyModal.onOpen();
      } else if (highlightIdx === -1 && results.length > 0) {
        handleSelect(results[0]);
      }
    } else if (e.key === 'Escape') {
      setEditing(false);
      setShowDropdown(false);
      setQuery('');
    }
  };

  const handleCompanyCreated = (company) => {
    handleSelect(company);
    companyModal.onClose();
  };

  // Close dropdown on outside click
  useEffect(() => {
    const handleClick = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target) &&
          inputRef.current && !inputRef.current.contains(e.target)) {
        setShowDropdown(false);
        setEditing(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  if (!editing) {
    return (
      <>
        <Tooltip label={value || ''} fontSize="xs" placement="top"
          isDisabled={!logoUrl || !value}>
          <Box
            cursor="pointer"
            onClick={() => { setEditing(true); setTimeout(() => inputRef.current?.focus(), 50); }}
            minH="20px"
            fontSize="12px"
            fontWeight={value ? '500' : '400'}
            color={value ? 'gray.800' : 'gray.400'}
            _hover={{ borderRadius: 'sm' }}
            px={1}
            py={0.5}
            textAlign="center"
          >
            {logoUrl ? (
              <Image src={logoUrl} maxH="28px" w="100%" objectFit="contain" mx="auto"
                fallback={<Text noOfLines={1}>{value || 'Select company...'}</Text>} />
            ) : (
              <Text noOfLines={1}>{value || 'Select company...'}</Text>
            )}
          </Box>
        </Tooltip>
        <CompanyModal
          isOpen={companyModal.isOpen}
          onClose={companyModal.onClose}
          initialName={pendingName}
          onCreated={handleCompanyCreated}
        />
      </>
    );
  }

  return (
    <Box position="relative">
      <Input
        ref={inputRef}
        size="xs"
        value={query}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        onBlur={() => setTimeout(() => { if (!companyModal.isOpen) { setEditing(false); setShowDropdown(false); } }, 200)}
        placeholder="Search company..."
        variant="flushed"
        fontSize="12px"
        px={2}
        
        
        
        
        
        
        autoFocus
      />
      {showDropdown && results.length > 0 && (
        <Box
          ref={dropdownRef}
          className="dropdown-popup"
          position="absolute"
          top="100%"
          left={0}
          right={0}
          bg="white"
          border="1px"
          borderColor="gray.200"
          
          boxShadow="lg"
          zIndex={9999}
          maxH="200px"
          overflowY="auto"
        >
          <List spacing={0}>
            {results.map((company, idx) => (
              <ListItem
                key={company.id}
                px={3}
                py={1.5}
                fontSize="12px"
                cursor="pointer"
                bg={highlightIdx === idx ? 'blue.50' : 'white'}
                _hover={{ bg: 'blue.50' }}
                onClick={() => handleSelect(company)}
              >
                <Flex align="center" gap={2}>
                  {company.logo_url && (
                    <Image src={`/api/files/${company.logo_url}`} maxH="16px" maxW="30px" objectFit="contain" flexShrink={0} fallback={null} />
                  )}
                  <Text fontWeight="500">{company.name}</Text>
                </Flex>
              </ListItem>
            ))}
          </List>
        </Box>
      )}
      {showDropdown && results.length === 0 && query.length > 0 && (
        <Box
          ref={dropdownRef}
          className="dropdown-popup"
          position="absolute"
          top="100%"
          left={0}
          right={0}
          bg="white"
          border="1px"
          borderColor="gray.200"
          
          boxShadow="lg"
          zIndex={9999}
          p={2}
        >
          <Text fontSize="11px" color="gray.500">
            No match. Press Enter to create "{query}"
          </Text>
        </Box>
      )}
      <CompanyModal
        isOpen={companyModal.isOpen}
        onClose={companyModal.onClose}
        initialName={pendingName}
        onCreated={handleCompanyCreated}
      />
    </Box>
  );
}
